import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { encontrarExcecaoEmConflito } from '../../../../natureza-operacao-excecao-escopo.util';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { IcmsExcecaoWizardDialogComponent, ExcecaoIcmsWizardDialogData, ExcecaoIcmsWizardDialogResult, GRUPOS_ESCOPO_DEF } from '../../excecao-wizard-dialog/excecao-wizard-dialog.component';
import { CstIcmsStruct } from '@shared/services/structs/motor-fiscal/cst-icms.struct';
import { NaturezaOperacaoIcmsExcecaoPendenteStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-icms-excecao-pendente.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { escopoLabels } from '../../../../escopo-contraparte.util';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { TipoEscopoEnum } from '@shared/enums/motor-fiscal/tipo-escopo.enum';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';

interface ExcecaoDisplayItem {
    _idx: number;
    escoposLabels: string[];
    cstIcms: string | null;
    cfopExcecao: string | null;
    vigenciaTexto: string;
}

@Component({
    selector: 'app-icms-tributo-excecoes-section',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatTableModule,
        NgxMaskPipe,
    ],
    providers: [provideNgxMask()],
    templateUrl: './tributo-excecoes-section.component.html',
    styleUrl: './tributo-excecoes-section.component.css',
})
export class IcmsTributoExcecoesSectionComponent implements OnChanges, OnInit {
    @Input() idNaturezaOperacao: number | null = null;
    @Input() codigoTributos: CstIcmsStruct[] = [];
    @Input() cfopLookupUrl = '';
    @Input() variaveis: VariavelStruct[] = [];
    @Input() formulasIcms: FormulaCatalogoItemStruct[] = [];
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Input() modalidadesBc: ModalidadeBcOption[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() motivosDesoneracao: LookupItemStruct[] = [];
    @Input() motivosReducaoAdRem: LookupItemStruct[] = [];
    @Input() excecoesPendentes: NaturezaOperacaoIcmsExcecaoPendenteStruct[] = [];
    @Output() excecoesChange = new EventEmitter<NaturezaOperacaoIcmsExcecaoPendenteStruct[]>();

    public localExcecoes: NaturezaOperacaoIcmsExcecaoPendenteStruct[] = [];
    public tiposEscopoLabels: Record<number, string> = {};
    public displayedColumns = ['escopos', 'cst', 'cfop', 'vigencia', 'actions'];

    public readonly cfopMaskPatterns = { '0': { pattern: /[0-9?]/ } };

    constructor(
        private dialog: MatDialog,
        private motorFiscalLookupService: MotorFiscalLookupService,
        private alertService: AlertService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['excecoesPendentes'] || changes['tipoOperacao']) {
            this.localExcecoes = [...this.excecoesPendentes];
            this.atualizarDisplay();
        }
    }

    ngOnInit(): void {
        this.motorFiscalLookupService.getTiposEscopo().subscribe(list => {
            this.tiposEscopoLabels = list.reduce((acc, item) => {
                acc[item.id] = item.descricao;
                return acc;
            }, {} as Record<number, string>);
            this.atualizarDisplay();
        });
    }

    private get escopoLabelsMap(): Record<number, string> {
        return escopoLabels(GRUPOS_ESCOPO_DEF, this.tipoOperacao, this.tiposEscopoLabels);
    }

    /**
     * Lista exibida na tabela. Mantida como campo estável (recalculado apenas
     * quando os dados mudam) — um getter recriaria o array a cada ciclo de
     * detecção de mudanças, forçando o mat-table a re-renderizar todas as linhas
     * e fechando/esvaziando o menu de ações ao ser acionado.
     */
    public excoesDisplay: ExcecaoDisplayItem[] = [];

    private atualizarDisplay(): void {
        this.excoesDisplay = this.localExcecoes.map((p, i) => ({
            _idx: i,
            escoposLabels: this.resolverEscoposLabels(p.req.escopos),
            cstIcms: p.req.cstIcms ?? null,
            cfopExcecao: p.req.cfopExcecao ?? null,
            vigenciaTexto: this.formatarVigencia(p.req.dataVigenciaInicio, p.req.dataVigenciaFim),
        }));
    }

    private resolverEscoposLabels(escopos: { tipoEscopo: TipoEscopoEnum }[]): string[] {
        const map = this.escopoLabelsMap;
        const labels: string[] = [];
        for (const e of escopos) {
            const lbl = map[e.tipoEscopo] ?? String(e.tipoEscopo);
            if (!labels.includes(lbl)) labels.push(lbl);
        }
        return labels;
    }

    private formatarVigencia(inicio: string, fim?: string | null): string {
        const ini = this.formatarData(inicio);
        const f = fim ? this.formatarData(fim) : 'Permanente';
        return `${ini} - ${f}`;
    }

    private formatarData(d: string): string {
        if (!d) return '';
        const [y, m, dia] = d.substring(0, 10).split('-');
        return `${dia}/${m}/${y}`;
    }

    public abrirWizardExcecao(): void {
        const data: ExcecaoIcmsWizardDialogData = {
            idNaturezaOperacao: this.idNaturezaOperacao ?? 0,
            idExcecao: null,
            excecaoData: null,
            codigoTributos: this.codigoTributos,
            cfopLookupUrl: this.cfopLookupUrl,
            variaveis: this.variaveis,
            formulas: this.formulasIcms,
            tipoOperacao: this.tipoOperacao,
            modalidadesBc: this.modalidadesBc,
            modalidadesBcSt: this.modalidadesBcSt,
            motivosDesoneracao: this.motivosDesoneracao,
            motivosReducaoAdRem: this.motivosReducaoAdRem,
        };
        const ref = this.dialog.open(IcmsExcecaoWizardDialogComponent, {
            width: '960px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });
        ref.afterClosed().subscribe((result: ExcecaoIcmsWizardDialogResult | null) => {
            if (result?.salvo && result.excecao) {
                const conflito = encontrarExcecaoEmConflito(result.excecao, this.localExcecoes.map(e => e.req));
                if (conflito) {
                    const nome = conflito.nome?.trim() || 'Sem nome';
                    this.alertService.show('Exceção conflitante', 'Esta exceção possui escopos conflitantes e vigência sobreposta à exceção "' + nome + '", sem critério de desempate. Ajuste antes de salvar a natureza.', AlertType.warning);
                }
                this.localExcecoes = [...this.localExcecoes, { idExcecao: null, req: result.excecao }];
                this.atualizarDisplay();
                this.excecoesChange.emit([...this.localExcecoes]);
            }
        });
    }

    public abrirWizardEditar(idx: number): void {
        const pendente = this.localExcecoes[idx];
        if (!pendente) return;
        const data: ExcecaoIcmsWizardDialogData = {
            idNaturezaOperacao: this.idNaturezaOperacao ?? 0,
            idExcecao: pendente.idExcecao ?? null,
            excecaoData: pendente.req,
            codigoTributos: this.codigoTributos,
            cfopLookupUrl: this.cfopLookupUrl,
            variaveis: this.variaveis,
            formulas: this.formulasIcms,
            tipoOperacao: this.tipoOperacao,
            modalidadesBc: this.modalidadesBc,
            modalidadesBcSt: this.modalidadesBcSt,
            motivosDesoneracao: this.motivosDesoneracao,
            motivosReducaoAdRem: this.motivosReducaoAdRem,
        };
        const ref = this.dialog.open(IcmsExcecaoWizardDialogComponent, {
            width: '960px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });
        ref.afterClosed().subscribe((result: ExcecaoIcmsWizardDialogResult | null) => {
            if (result?.salvo && result.excecao) {
                const conflito = encontrarExcecaoEmConflito(result.excecao, this.localExcecoes.filter((_, i) => i !== idx).map(e => e.req));
                if (conflito) {
                    const nome = conflito.nome?.trim() || 'Sem nome';
                    this.alertService.show('Exceção conflitante', 'Esta exceção possui escopos conflitantes e vigência sobreposta à exceção "' + nome + '", sem critério de desempate. Ajuste antes de salvar a natureza.', AlertType.warning);
                }
                const updated = [...this.localExcecoes];
                updated[idx] = { idExcecao: pendente.idExcecao, req: result.excecao };
                this.localExcecoes = updated;
                this.atualizarDisplay();
                this.excecoesChange.emit([...this.localExcecoes]);
            }
        });
    }

    public excluirExcecao(idx: number): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar Exclusão',
                description: 'Você tem certeza que deseja excluir esta exceção? Esta ação não poderá ser desfeita.',
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                this.localExcecoes = this.localExcecoes.filter((_, i) => i !== idx);
                this.atualizarDisplay();
                this.excecoesChange.emit([...this.localExcecoes]);
            }
        });
    }
}
